/**
 * Shared transformation helpers for visualizations.
 *
 * Used by cartesian (bar/line/area), heatmap, and scatter visualizations.
 * Pure functions — testable independently of React.
 */

import type { ColumnDef, ColumnType } from '@deckspace/types';
import { DeckspaceError } from '@deckspace/errors';

/** Table data in columnar format (as returned from SQL queries). */
export interface TableData {
  columns: string[];
  rows: unknown[][];
}

/**
 * Single numeric series consumed by visualization components.
 * Local to ui-components — replaces the legacy `Dataset` type from @deckspace/types.
 */
export interface Series {
  label: string;
  values: number[];
  /** Column data type. Defaults to 'number' at construction. */
  type: ColumnType;
  /** Unit for number columns (e.g., 'kg', 'km', 'mph'). */
  unit?: string;
  /** ISO 4217 currency code for currency columns (e.g., 'USD', 'EUR'). */
  currency?: string;
}

/** Result of pivoting long-format rows into multi-series labels + datasets. */
export interface PivotResult {
  labels: string[];
  datasets: Series[];
}

/** Convert row objects to columnar format. */
export function rowsToColumnar(
  rowObjects: Record<string, unknown>[],
  columns?: string[]
): TableData {
  if (rowObjects.length === 0) {
    return { columns: columns ?? [], rows: [] };
  }

  const first = rowObjects[0];
  const cols = columns ?? (first ? Object.keys(first) : []);
  const rows = rowObjects.map((row) => cols.map((col) => row[col]));

  return { columns: cols, rows };
}

/** Get a column's values from columnar table data. */
export function getColumn<T>(table: TableData, columnName: string): T[] {
  const idx = table.columns.indexOf(columnName);
  if (idx === -1) {
    throw new DeckspaceError('COLUMN_NOT_FOUND', `Column "${columnName}" not found in table`);
  }
  return table.rows.map((row) => row[idx] as T);
}

/** Build a `columnName → ColumnDef` lookup from a `ColumnDef[]`. */
export function defsByName(columnDefs: ColumnDef[]): Record<string, ColumnDef> {
  const out: Record<string, ColumnDef> = {};
  for (const def of columnDefs) out[def.name] = def;
  return out;
}

/**
 * Comparator producing a stable sort by ColumnType semantics.
 *
 * Storage order is not a Deckspace promise; axis order is intrinsic to the
 * visualization and resolved from the column's `ColumnType` at render time.
 *
 * - `date`                        → temporal ASC via Date.parse (ISO/RFC-2822/etc.)
 * - `number`/`currency`/`percent` → numeric ASC
 * - `text`/`boolean`/`url`/missing → preserve insertion order (stable sort)
 *
 * Text is intentionally not sorted: text labels are semantically ambiguous
 * (e.g., month names "Jan/Feb/Mar" are chronological, not alphabetical;
 * "Region 1/2/10" wants natural-sort; arbitrary tags have no natural order).
 * Opt-in text sort modes are a Catalog Unification follow-up.
 *
 * Unparseable values inside a numeric or date column fall back to lexical
 * compare rather than producing NaN orderings.
 */
function compareByType(a: string, b: string, type: ColumnType | undefined): number {
  switch (type) {
    case 'date': {
      const ta = Date.parse(a);
      const tb = Date.parse(b);
      if (Number.isNaN(ta) || Number.isNaN(tb)) return a < b ? -1 : a > b ? 1 : 0;
      return ta - tb;
    }
    case 'number':
    case 'currency':
    case 'percent': {
      const na = Number(a);
      const nb = Number(b);
      if (Number.isNaN(na) || Number.isNaN(nb)) return a < b ? -1 : a > b ? 1 : 0;
      return na - nb;
    }
    default:
      return 0;
  }
}

/**
 * Indices that would sort `values` in natural axis order for the given column type.
 *
 * Use with `permute()` to reorder a parallel data array in the same way.
 * Array.prototype.sort on indices is stable in V8/SpiderMonkey/JSC,
 * so equal-comparing entries (including the boolean/url/default case
 * where the comparator always returns 0) preserve insertion order.
 */
export function sortIndicesByType(values: string[], type: ColumnType | undefined): number[] {
  const indices = values.map((_, i) => i);
  indices.sort((i, j) => compareByType(values[i]!, values[j]!, type));
  return indices;
}

/** Reorder `arr` so position k holds the element previously at indices[k]. */
export function permute<T>(arr: readonly T[], indices: readonly number[]): T[] {
  return indices.map((i) => arr[i]!);
}

/**
 * Pivot long-format table data into multi-series datasets.
 *
 * Transforms rows like:
 *   month | region | revenue
 *   Jan   | North  | 100
 *   Jan   | South  | 200
 *
 * Into: { labels: ["Jan"], datasets: [{ label: "North", values: [100] }, ...] }
 *
 * Missing combinations are filled with 0. Labels are sorted by the `xKey`
 * column's `ColumnType` (date → temporal, number-family → numeric, text →
 * natural-sort); other types preserve insertion order. Groups always
 * preserve insertion order — categorical legends have no canonical order.
 */
export function pivotByGroup(
  tableData: TableData,
  xKey: string,
  yKey: string,
  groupKey: string,
  columnDefsByName?: Record<string, ColumnDef>
): PivotResult {
  const { columns, rows } = tableData;

  const xIdx = columns.indexOf(xKey);
  const yIdx = columns.indexOf(yKey);
  const gIdx = columns.indexOf(groupKey);

  if (xIdx === -1) {
    throw new DeckspaceError('COLUMN_NOT_FOUND', `Column "${xKey}" not found in table`);
  }
  if (yIdx === -1) {
    throw new DeckspaceError('COLUMN_NOT_FOUND', `Column "${yKey}" not found in table`);
  }
  if (gIdx === -1) {
    throw new DeckspaceError('COLUMN_NOT_FOUND', `Column "${groupKey}" not found in table`);
  }

  // Collect unique labels and groups in insertion order
  const labelSet = new Set<string>();
  const groupSet = new Set<string>();
  for (const row of rows) {
    labelSet.add(String(row[xIdx]));
    groupSet.add(String(row[gIdx]));
  }
  const insertionLabels = [...labelSet];
  const groups = [...groupSet];

  // Resolve axis order from the x column's ColumnType.
  const labelOrder = sortIndicesByType(insertionLabels, columnDefsByName?.[xKey]?.type);
  const labels = permute(insertionLabels, labelOrder);

  // Build value lookup: group -> label -> value
  const valueLookup = new Map<string, Map<string, number>>();
  for (const group of groups) {
    valueLookup.set(group, new Map());
  }
  // Duplicate (label, group) pairs: last value wins
  for (const row of rows) {
    const group = String(row[gIdx]);
    const label = String(row[xIdx]);
    const value = Number(row[yIdx]) || 0;
    const groupMap = valueLookup.get(group);
    if (groupMap) groupMap.set(label, value);
  }

  // Build datasets — dimension/unit come from the y column since it holds the values
  const datasets = groups.map((group) => {
    const groupMap = valueLookup.get(group);
    return {
      label: group,
      values: labels.map((l) => groupMap?.get(l) ?? 0),
      type: columnDefsByName?.[yKey]?.type ?? 'number',
      unit: columnDefsByName?.[yKey]?.unit,
      currency: columnDefsByName?.[yKey]?.currency,
    };
  });

  return { labels, datasets };
}
